import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getDeletedRecords, restoreDeletedRecords, permanentDeleteRecords } from '../lib/api';
import { formatCurrency, formatDate, formatApiErrorDetail } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Checkbox } from '../components/ui/checkbox';
import { ExportToolbar } from '../components/ui/export-toolbar';
import { SearchableSelect } from '../components/ui/searchable-select';
import { RotateCcw, Trash2, X } from 'lucide-react';
import { useSortableTable, SortableHeader } from '../components/ui/sortable-table';
import { toast } from 'sonner';

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'income', label: 'Income' },
  { value: 'expense', label: 'Expense' },
  { value: 'sales', label: 'Sales' },
  { value: 'customers', label: 'Customers' },
  { value: 'recurring', label: 'Recurring' },
  { value: 'user_transfer', label: 'User Transfers' },
  { value: 'entry_ledgers', label: 'Entry Ledgers' },
  { value: 'purchase_orders', label: 'Purchase Orders' },
  { value: 'purchase_entries', label: 'Purchase Entries' },
  { value: 'suppliers', label: 'Suppliers' },
  { value: 'products', label: 'Products' },
  { value: 'supplier_groups', label: 'Supplier Groups' },
  { value: 'batches', label: 'Batches' }
];

const TYPE_COLORS = {
  income: 'default',
  expense: 'destructive',
  sales: 'secondary',
  customers: 'outline',
  recurring: 'secondary',
  user_transfer: 'default',
  entry_ledgers: 'outline',
  purchase_orders: 'default',
  purchase_entries: 'destructive',
  suppliers: 'secondary',
  products: 'outline',
  supplier_groups: 'secondary',
  batches: 'outline'
};

const TYPE_LABELS = {
  income: 'Income',
  expense: 'Expense',
  sales: 'Sales',
  customers: 'Customer',
  recurring: 'Recurring',
  user_transfer: 'Transfer',
  entry_ledgers: 'Entry Ledger',
  purchase_orders: 'PO',
  purchase_entries: 'PE',
  suppliers: 'Supplier',
  products: 'Product',
  supplier_groups: 'Group',
  batches: 'Batch'
};

const DeletedRecords = () => {
  const { isAdmin } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filterType, setFilterType] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setSelectedIds([]);
    try {
      const res = await getDeletedRecords(filterType || undefined);
      setRecords(res.data);
    } catch (err) { toast.error('Failed to fetch deleted records'); } finally { setLoading(false); }
  }, [filterType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const { sortedData, sortKey, sortDirection, handleSort } = useSortableTable(records, 'deleted_at', 'desc');

  const allSelected = sortedData.length > 0 && sortedData.every(r => selectedIds.includes(r.id));
  const toggleSelectAll = () => {
    if (allSelected) setSelectedIds([]);
    else setSelectedIds(sortedData.map(r => r.id));
  };

  const handleRestore = async (ids) => {
    if (!window.confirm(`Restore ${ids.length} record(s)? Balance changes will be reversed.`)) return;
    setRestoring(true);
    try {
      const res = await restoreDeletedRecords(ids);
      toast.success(res.data.message);
      setSelectedIds([]);
      fetchData();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setRestoring(false); }
  };

  const handlePermanentDelete = async (ids) => {
    if (!window.confirm(`Permanently delete ${ids.length} record(s)? This cannot be undone.`)) return;
    setRestoring(true);
    try {
      const res = await permanentDeleteRecords(ids);
      toast.success(res.data.message);
      setSelectedIds([]);
      fetchData();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setRestoring(false); }
  };

  if (!isAdmin) {
    return <div className="text-center py-12 text-muted-foreground">Admin access required</div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="deleted-records-page">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Deleted Records</h1>
          <p className="text-muted-foreground text-sm">View and restore soft-deleted records</p>
        </div>
        <ExportToolbar endpoint="/exports/deleted-records" fileBaseName="deleted-records" params={{ type: filterType || undefined }} testId="export-deleted" />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="w-48">
          <SearchableSelect
            options={TYPE_OPTIONS}
            value={filterType}
            onChange={setFilterType}
            placeholder="Filter by type"
            data-testid="deleted-type-filter"
          />
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-md border bg-card shrink-0">
          <Trash2 className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Total:</span>
          <span className="text-lg font-bold font-mono">{records.length}</span>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.length > 0 && (
        <div className="fixed bottom-14 sm:bottom-14 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 px-5 py-3 rounded-full shadow-lg border bg-background" data-testid="restore-action-bar">
          <span className="text-sm font-medium">{selectedIds.length} selected</span>
          <Button variant="outline" size="sm" onClick={() => setSelectedIds([])} data-testid="clear-restore-selection-btn">Clear</Button>
          <Button size="sm" onClick={() => handleRestore(selectedIds)} disabled={restoring} data-testid="bulk-restore-btn">
            <RotateCcw className="h-4 w-4 mr-1" /><span className="hidden sm:inline">{restoring ? 'Restoring...' : 'Restore'}</span><span className="sm:hidden">Restore</span>
          </Button>
          <Button variant="destructive" size="sm" onClick={() => handlePermanentDelete(selectedIds)} disabled={restoring} data-testid="bulk-permanent-delete-btn">
            <X className="h-4 w-4 mr-1" /><span className="hidden sm:inline">Delete Permanently</span><span className="sm:hidden">Delete</span>
          </Button>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10 cursor-pointer" onClick={toggleSelectAll}>
                    <Checkbox checked={allSelected} className="pointer-events-none" data-testid="select-all-deleted" aria-label="Select all" />
                  </TableHead>
                  <TableHead><SortableHeader label="Type" sortKey="record_type" currentSortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                  <TableHead><SortableHeader label="Date" sortKey="date" currentSortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                  <TableHead className="hidden sm:table-cell"><SortableHeader label="Category / Name" sortKey="category" currentSortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                  <TableHead className="text-right"><SortableHeader label="Amount" sortKey="amount" currentSortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} className="justify-end" /></TableHead>
                  <TableHead className="hidden sm:table-cell">Remarks</TableHead>
                  <TableHead className="hidden sm:table-cell"><SortableHeader label="Deleted By" sortKey="deleted_by" currentSortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                  <TableHead className="hidden sm:table-cell"><SortableHeader label="Deleted At" sortKey="deleted_at" currentSortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                  <TableHead className="text-right w-[60px] sm:w-auto">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-8"><Skeleton className="h-6 w-32 mx-auto" /></TableCell></TableRow>
                ) : records.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No deleted records found</TableCell></TableRow>
                ) : sortedData.map(record => (
                  <React.Fragment key={record.id}>
                    <TableRow
                      data-testid={`deleted-row-${record.id}`}
                      className={`cursor-pointer sm:cursor-default ${selectedIds.includes(record.id) ? 'bg-muted/50' : ''} ${expandedId === record.id ? 'bg-muted/30' : ''}`}
                      onClick={() => setExpandedId(expandedId === record.id ? null : record.id)}
                    >
                      <TableCell className="cursor-pointer" onClick={(e) => { e.stopPropagation(); toggleSelect(record.id); }}>
                        <Checkbox checked={selectedIds.includes(record.id)} className="pointer-events-none" data-testid={`select-deleted-${record.id}`} />
                      </TableCell>
                      <TableCell><Badge variant={TYPE_COLORS[record.record_type] || 'secondary'} className="text-[10px] px-1.5">{TYPE_LABELS[record.record_type] || record.record_type}</Badge></TableCell>
                      <TableCell className="text-sm">{record.date ? formatDate(record.date.substring(0, 10)) : '-'}</TableCell>
                      <TableCell className="hidden sm:table-cell font-medium max-w-[200px] truncate">{record.category || '-'}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{record.amount > 0 ? formatCurrency(record.amount) : '-'}</TableCell>
                      <TableCell className="hidden sm:table-cell max-w-[150px] truncate">{record.remarks || '-'}</TableCell>
                      <TableCell className="hidden sm:table-cell text-sm">{record.deleted_by}</TableCell>
                      <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">{record.deleted_at ? formatDate(record.deleted_at.substring(0, 10)) : '-'}</TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-0.5 sm:gap-1">
                          <Button variant="outline" size="sm" className="h-7 px-1.5 sm:px-2" onClick={() => handleRestore([record.id])} disabled={restoring} data-testid={`restore-${record.id}`}>
                            <RotateCcw className="h-3 w-3 sm:mr-1" /><span className="hidden sm:inline">Restore</span>
                          </Button>
                          <Button variant="destructive" size="sm" className="h-7 px-1.5 sm:px-2" onClick={() => handlePermanentDelete([record.id])} disabled={restoring} data-testid={`perm-delete-${record.id}`}>
                            <X className="h-3 w-3 sm:mr-1" /><span className="hidden sm:inline">Delete</span>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {/* Mobile expanded detail */}
                    {expandedId === record.id && (
                      <TableRow className="sm:hidden bg-muted/20">
                        <TableCell colSpan={9} className="py-2 px-4 space-y-1 text-sm">
                          <div><span className="text-muted-foreground">Category:</span> <span className="font-medium">{record.category || '-'}</span></div>
                          {record.remarks && <div><span className="text-muted-foreground">Remarks:</span> {record.remarks}</div>}
                          <div><span className="text-muted-foreground">Deleted By:</span> {record.deleted_by}</div>
                          <div><span className="text-muted-foreground">Deleted At:</span> {record.deleted_at ? formatDate(record.deleted_at.substring(0, 10)) : '-'}</div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DeletedRecords;
